/**
 * Badge — small inline status chip. Variants: default, secondary, success, warning,
 * destructive, info, outline.
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold tracking-wide transition-colors',
  {
    variants: {
      variant: {
        default:
          'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300',
        secondary:
          'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300',
        success:
          'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
        warning:
          'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400',
        destructive:
          'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400',
        info:
          'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400',
        outline:
          'border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export default function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// eslint-disable-next-line react-refresh/only-export-components
export { badgeVariants };
